<script setup>
import { onMounted, onUnmounted, ref, watch, computed, provide, defineAsyncComponent } from 'vue'
import { useTrackingStore } from './store/tracking'
import { RouterView, useRoute, useRouter } from 'vue-router'
import CookieBanner from './components/CookieBanner.vue'
import GlobalSearch from './components/GlobalSearch.vue'
import { useAnalytics } from './composables/useAnalytics'
import { useAuthStore } from './stores/authStore'
import { useCartStore } from './stores/cartStore'
import { useSettingsStore } from './stores/settingsStore'
import LoginModal from './components/LoginModal.vue'
import BackToTop from './components/BackToTop.vue'
import AIProductRecommendation from './components/AIProductRecommendation.vue'
import ToastContainer from './components/ToastContainer.vue'
import ConfirmModal from './components/ConfirmModal.vue'
import CompareFloatingBar from './components/CompareFloatingBar.vue'
import HistoryFloatingButton from './components/HistoryFloatingButton.vue'
import ContactFloatingButton from './components/ContactFloatingButton.vue'
import Maintenance from './pages/Maintenance.vue'

// Auth state
const authStore = useAuthStore()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()

const formattedFooterBannerTitle = computed(() => {
  const raw = settingsStore.footerBannerTitle || 'เริ่มต้นสร้าง <span class="text-emerald-800 dark:text-emerald-400">ผลผลิตที่ดีกว่า</span>'
  return raw.replace(/text-emerald-500/g, 'text-emerald-800 dark:text-emerald-400')
            .replace(/text-emerald-600/g, 'text-emerald-800 dark:text-emerald-400')
})
const isLoginModalOpen = ref(false)
provide('openLoginModal', () => { isLoginModalOpen.value = true })

const desktopAvatarError = ref(false)
const mobileAvatarError = ref(false)

watch(() => authStore.currentUser?.avatar_url, () => {
  desktopAvatarError.value = false
  mobileAvatarError.value = false
})

// Floating contact hub state
const isContactHubOpen = ref(false)

// Search state
const isSearchOpen = ref(false)

// Nav categories dropdown
const navCategories = ref([])

// Map category name keywords -> icon key (for dynamic API categories)
const getCategoryIcon = (name = '') => {
  const n = name.toLowerCase()
  if (n.includes('hdpe')) return 'hdpe-shed'
  if (n.includes(' pp') || n.startsWith('pp')) return 'pp-shed'
  if (n.includes('โลหะ') || n.includes('เหล็ก') || n.includes('metal')) return 'metal-shed'
  if (n.includes('โรงเรือน') || n.includes('greenhouse') || n.includes('green house')) return 'greenhouse'
  if (n.includes('โรงจอดรถ') || n.includes('carport') || n.includes('จอดรถ')) return 'carport'
  if (n.includes('เครื่องมือ') || n.includes('toolbox') || n.includes('tool')) return 'toolbox'
  if (n.includes('กลางแจ้ง') || n.includes('outdoor')) return 'outdoor-box'
  if (n.includes('ตู้') || n.includes('cabinet')) return 'plastic-cabinet'
  if (n.includes('อุปกรณ์') || n.includes('accessories') || n.includes('อะไหล่')) return 'accessories'
  if (n.includes('บ้านเก็บ') || n.includes('shed')) return 'hdpe-shed'
  return 'default'
}
const isProductsDropdownOpen = ref(false)
const isMobileProductsOpen = ref(false)
let dropdownTimer = null

const openProductsDropdown = () => {
  clearTimeout(dropdownTimer)
  isProductsDropdownOpen.value = true
}
const closeProductsDropdown = () => {
  dropdownTimer = setTimeout(() => {
    isProductsDropdownOpen.value = false
  }, 200)
}

const isProfileDropdownOpen = ref(false)
let profileTimer = null
const openProfileDropdown = () => {
  clearTimeout(profileTimer)
  isProfileDropdownOpen.value = true
}
const closeProfileDropdown = () => {
  profileTimer = setTimeout(() => {
    isProfileDropdownOpen.value = false
  }, 200)
}
const contactPhones = computed(() => settingsStore.contactPhones)
const contactEmails = computed(() => settingsStore.contactEmails)
const contactLines = computed(() => settingsStore.contactLines)
const contactFacebookUrl = computed(() => settingsStore.contactFacebookUrl)
const contactTiktokUrl = computed(() => settingsStore.contactTiktokUrl)
const contactYoutubeUrl = computed(() => settingsStore.contactYoutubeUrl)

// Dynamic Footer defaults & computed properties
const defaultMainMenu = [
  { name: 'หน้าแรก', link: '/' },
  { name: 'สินค้าทั้งหมด', link: '/products' },
  { name: 'บริการของเรา', link: '/services' },
  { name: 'คำถามที่พบบ่อย', link: '/faq' },
  { name: 'ผลงาน', link: '/projects' }
]

const defaultHelpMenu = [
  { name: 'เกี่ยวกับเรา', link: '/about' },
  { name: 'ติดต่อเรา', link: '/contact' },
  { name: 'ตัวแทนจำหน่าย', link: '/contact' },
  { name: 'นโยบายความเป็นส่วนตัว', link: '/privacy-policy' },
  { name: 'เงื่อนไขการใช้งาน', link: '/terms-of-service' }
]

const mainMenuLinks = computed(() => {
  return settingsStore.footerMainMenu && settingsStore.footerMainMenu.length > 0
    ? settingsStore.footerMainMenu
    : defaultMainMenu
})

const helpMenuLinks = computed(() => {
  return settingsStore.footerHelpMenu && settingsStore.footerHelpMenu.length > 0
    ? settingsStore.footerHelpMenu
    : defaultHelpMenu
})

const copyrightName = computed(() => {
  return settingsStore.footerCopyrightName || 'GreenHouse Smart Farming Solutions'
})

// Newsletter state
const newsletterEmail = ref('')
const newsletterHoneypot = ref('')
const newsletterTimestamp = ref(Date.now())
const newsletterLoading = ref(false)
const newsletterSuccess = ref(false)
const newsletterError = ref('')

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const submitNewsletter = async () => {
  newsletterError.value = ''
  if (!newsletterEmail.value.trim()) {
    newsletterError.value = 'กรุณากรอกอีเมลของคุณ'
    return
  }
  if (!isValidEmail(newsletterEmail.value.trim())) {
    newsletterError.value = 'รูปแบบอีเมลไม่ถูกต้อง'
    return
  }
  newsletterLoading.value = true
  try {
    const res = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: newsletterEmail.value.trim(),
        honeypot: newsletterHoneypot.value,
        timestamp: newsletterTimestamp.value
      })
    })
    const data = await res.json()
    if (data.success) {
      newsletterSuccess.value = true
      newsletterEmail.value = ''
    } else {
      newsletterError.value = data.error || 'เกิดข้อผิดพลาด'
    }
  } catch (e) {
    newsletterError.value = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้'
  } finally {
    newsletterLoading.value = false
  }
}

// Initialize tracking
const trackingStore = useTrackingStore()
const route = useRoute()
const router = useRouter()

// Watch authentication status for redirection
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    if (route.path !== '/cart' && route.path !== '/checkout') {
      router.push('/profile')
    }
  } else {
    if (route.path === '/profile' || route.path === '/checkout') {
      router.push('/')
    }
  }
})

// Watch route query to open login modal automatically (e.g. from redirect links)
watch(() => route.query.login, (val) => {
  if (val === 'true') {
    isLoginModalOpen.value = true
    // Clean up the query parameter to avoid opening it again on reload/navigation
    const query = { ...route.query }
    delete query.login
    router.replace({ path: route.path, query })
  }
}, { immediate: true })


// Initialize detailed analytics (auto-tracks pageviews, scroll depth, time on page)
const { trackPageView } = useAnalytics()

// Re-track on route change
watch(() => route.fullPath, () => {
  if (!route.path.startsWith('/admin')) {
    trackPageView()
  }
})

// Dark Mode Logic
const isDark = ref(false)
const isMobileMenuOpen = ref(false)
const isScrolled = ref(false)

const handleScroll = () => {
  if (window.scrollY > 50) {
    isScrolled.value = true
  } else {
    isScrolled.value = false
  }
}

const isDarkHeader = computed(() => {
  const darkPages = []
  const isDarkPage = darkPages.includes(route.path) || (route.path.startsWith('/projects/') && route.path !== '/projects')
  return isDarkPage && !isScrolled.value
})


const isAdminRoute = computed(() => {
  return route.path.startsWith('/admin') || window.location.pathname.startsWith('/admin')
})

// Active route detection for nav highlighting
const isActiveRoute = (path) => {
  if (path === '/') return route.path === '/'
  return route.path === path || route.path.startsWith(path + '/')
}

const toggleDarkMode = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

// Close mobile menu on route change
watch(route, () => {
  isMobileMenuOpen.value = false
})

onMounted(() => {
  // Global image error fallback: when cached thumbnails 404, fall back to original images
  document.addEventListener('error', (event) => {
    const img = event.target
    if (!img || img.tagName !== 'IMG') return

    const src = img.getAttribute('src') || ''
    // Only handle /uploads/cache/ URLs
    if (!src.includes('/uploads/') || !src.includes('/cache/')) return
    // Prevent infinite loops
    if (img.dataset.fallbackAttempted === 'original') return

    // Parse cache path: /uploads[/subfolder]/cache/basename-WIDTH.ext
    const cacheMatch = src.match(/^\/uploads\/(?:(.+)\/)?cache\/([^/]+)-(\d+)\.([a-zA-Z0-9]+)$/)
    if (!cacheMatch) return

    if (img.dataset.fallbackAttempted !== 'resize-api') {
      // First fallback: try the on-the-fly resize API
      const cachePath = src.startsWith('/') ? src.substring(1) : src
      img.dataset.fallbackAttempted = 'resize-api'
      img.src = `/api/upload/resize?path=${encodeURIComponent(cachePath)}`
      return
    }

    // Second fallback: use the original full-size image
    const subfolder = cacheMatch[1] || ''
    const baseName = cacheMatch[2]
    const ext = cacheMatch[4]
    const originalPath = subfolder
      ? `/uploads/${subfolder}/${baseName}.${ext}`
      : `/uploads/${baseName}.${ext}`
    img.dataset.fallbackAttempted = 'original'
    img.src = originalPath
  }, true) // 'true' = capture phase, intercepts before component-level handlers


  // Capture Registration Source
  if (!localStorage.getItem('registration_source')) {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      let source = 'organic'
      if (urlParams.get('utm_source') || urlParams.get('utm_medium')) {
        source = `utm_source=${urlParams.get('utm_source') || 'unknown'}&utm_medium=${urlParams.get('utm_medium') || 'none'}`
      } else if (urlParams.get('fbclid')) {
        source = 'facebook_ad'
      } else if (urlParams.get('ref')) {
        source = `ref=${urlParams.get('ref')}`
      } else if (document.referrer && !document.referrer.includes(window.location.hostname)) {
        source = new URL(document.referrer).hostname
      }
      localStorage.setItem('registration_source', source)
    } catch (e) {
      console.error('Failed to parse registration source', e)
    }
  }

  // Initialize Session
  trackingStore.initSession()
  trackingStore.trackEvent({ type: 'page_view', path: window.location.pathname })

  // Initialize Cart
  if (!window.location.pathname.startsWith('/admin')) {
    cartStore.fetchCart()
  }

  // Load categories for nav dropdown
  fetch('/api/categories')
    .then(r => r.json())
    .then(data => { if (data.success) navCategories.value = data.data })
    .catch(e => console.error('Failed to load nav categories', e))
  
  // Initialize Theme (Check LocalStorage or System Prefs)
  if (localStorage.getItem('theme') === 'dark') {
    isDark.value = true
    document.documentElement.classList.add('dark')
  } else {
    isDark.value = false
    document.documentElement.classList.remove('dark')
  }
  // Handle Scroll
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div class="min-h-screen flex flex-col font-sans transition-colors duration-500 bg-[#f8f9fa] dark:bg-[#0a0f16] text-gray-900 dark:text-gray-100 selection:bg-emerald-200 dark:selection:bg-emerald-800/50">
    
    <!-- Maintenance Mode -->
    <Maintenance v-if="settingsStore.maintenanceModeEnabled && !isAdminRoute" />

    <template v-else>
      <!-- Holiday Banner is now inside Home.vue -->

    <!-- Clean Fixed Navigation Header -->
    <header v-if="!isAdminRoute"
      :class="[
        'fixed top-0 left-0 w-full z-50 transition-colors duration-300',
        isScrolled 
          ? 'bg-white/95 dark:bg-[#0a0f16]/95 backdrop-blur-md shadow-sm border-b border-gray-200/80 dark:border-gray-800' 
          : (isDarkHeader ? 'bg-transparent' : 'bg-white/90 dark:bg-[#0a0f16]/90 backdrop-blur-md border-b border-gray-100/80 dark:border-gray-800/50')
      ]"
    >
      <div class="mx-auto flex justify-between items-center px-6 lg:px-8 py-3.5 max-w-[90rem]">
        
        <!-- Logo -->
        <router-link to="/" class="group flex items-center gap-2.5 z-50 select-none">
          <template v-if="settingsStore.storeLogo">
            <img :src="settingsStore.storeLogo" alt="Logo" width="160" height="36" class="h-9 w-auto object-contain" />
          </template>
          <template v-else>
            <div :class="['w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0', isDarkHeader ? 'bg-white/10' : 'bg-emerald-50/10 dark:bg-emerald-950/10']">
              <svg :class="['w-8 h-8', isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400']" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 15 L85 45 L85 85 L15 85 L15 45 Z" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M50 15 L50 85" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                <path d="M15 50 L85 50" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                <path d="M50 55 C55 45 65 50 60 60 C55 67 50 70 50 70 C50 70 45 67 40 60 C35 50 45 45 50 55 Z" fill="#10b981" />
              </svg>
            </div>
            <div class="flex items-center gap-0.5">
              <span :class="['text-base font-extrabold leading-none tracking-tight', isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400']">โรงเรือนปลูกพืช</span>
              <span class="text-base font-extrabold text-amber-500 leading-none tracking-tight">.com</span>
            </div>
          </template>
        </router-link>
        
        <!-- Desktop Nav -->
        <nav class="hidden lg:flex items-center gap-5 xl:gap-8 mx-auto">
          <router-link to="/" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate', route.path === '/' ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            หน้าแรก
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', route.path === '/' ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>

          <!-- Products with Dropdown -->
          <div class="relative" @mouseenter="openProductsDropdown" @mouseleave="closeProductsDropdown">
            <router-link to="/products" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate inline-flex items-center gap-1.5', isActiveRoute('/products') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
              สินค้า
              <svg class="w-3 h-3 transition-transform duration-200" :class="isProductsDropdownOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7"/></svg>
              <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/products') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
            </router-link>
            <!-- Dropdownmega menu -->
            <transition name="dropdown">
              <div v-if="isProductsDropdownOpen" class="absolute top-full left-1/2 -translate-x-1/2 pt-4 z-50">
                <div class="bg-white dark:bg-[#0c121e] rounded-3xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] border border-gray-100/80 dark:border-white/5 w-[600px] overflow-hidden flex flex-row">
                  <!-- Left Banner Card -->
                  <div class="relative w-[200px] shrink-0 p-6 bg-gradient-to-b from-[#e8f3ec]/90 via-[#eef6f1]/60 to-[#f5faf6]/90 dark:from-[#112419] dark:via-[#0b1710] dark:to-[#08100b] flex flex-col justify-between items-center text-center overflow-hidden border-r border-gray-100/50 dark:border-white/5 select-none">
                    <!-- SVG Leaves branch illustration on left top -->
                    <svg class="absolute top-[-15px] left-[-20px] w-32 h-48 text-emerald-600/10 dark:text-emerald-500/5 pointer-events-none transform -rotate-12" viewBox="0 0 120 180" fill="currentColor">
                      <path d="M0 0 C30 40, 40 80, 10 130 C5 140, 0 160, 0 180" fill="none" stroke="currentColor" stroke-width="2" />
                      <path d="M15 30 C30 25, 45 35, 40 45 C35 50, 20 45, 15 30 Z" />
                      <path d="M28 60 C45 55, 55 68, 48 75 C40 80, 30 72, 28 60 Z" />
                      <path d="M26 95 C40 95, 48 110, 38 118 C30 120, 24 108, 26 95 Z" />
                      <path d="M12 125 C25 130, 30 148, 20 154 C10 156, 8 142, 12 125 Z" />
                      <path d="M5 15 C15 5, 25 15, 20 22 C15 25, 8 20, 5 15 Z" />
                    </svg>

                    <!-- Circular Store/Greenhouse Logo in center -->
                    <div class="flex flex-col items-center">
                      <div class="w-[56px] h-[56px] rounded-full bg-emerald-50/50 dark:bg-emerald-950/20 flex items-center justify-center p-1.5 border border-emerald-500/10 shadow-sm mt-2">
                        <div class="w-full h-full rounded-full bg-white dark:bg-slate-900 border border-emerald-500/20 flex items-center justify-center shadow-inner">
                          <svg class="w-7 h-7 text-emerald-600 dark:text-emerald-400" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M50 15 L85 45 L85 85 L15 85 L15 45 Z" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M50 15 L50 85" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                            <path d="M15 50 L85 50" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                            <path d="M50 55 C55 45 65 50 60 60 C55 67 50 70 50 70 C50 70 45 67 40 60 C35 50 45 45 50 55 Z" fill="#10b981" />
                          </svg>
                        </div>
                      </div>

                      <!-- Text Title -->
                      <div class="mt-4 flex flex-col items-center">
                        <span class="text-base font-extrabold text-emerald-700 dark:text-emerald-400 leading-tight">สินค้าทั้งหมด</span>
                        <span class="text-base font-extrabold text-slate-800 dark:text-slate-100 leading-tight mt-0.5">ของเรา</span>
                      </div>

                      <!-- Subtitle lists -->
                      <div class="text-[10px] text-gray-500 dark:text-gray-400 font-semibold leading-relaxed mt-3 space-y-0.5">
                        <div>วัสดุคุณภาพ แข็งแรง ทนทาน</div>
                        <div>ออกแบบเพื่อการใช้งาน</div>
                        <div>ที่ยาวนาน</div>
                      </div>
                    </div>

                    <!-- Button 'ดูสินค้าทั้งหมด' -->
                    <router-link to="/products" class="w-full bg-white dark:bg-[#0c121e] hover:bg-emerald-50/50 dark:hover:bg-slate-900 text-emerald-700 dark:text-emerald-400 font-bold border border-emerald-600/15 dark:border-emerald-500/30 py-2 px-3 rounded-full flex items-center justify-center gap-1.5 transition-all duration-300 shadow-sm mt-5 active:scale-[0.98] text-xs">
                      <span>ดูสินค้าทั้งหมด</span>
                      <svg class="w-3 h-3 transform translate-y-[0.5px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </router-link>
                  </div>

                  <!-- Right Column List of Categories (dynamic from admin) -->
                  <div class="flex-1 flex flex-col divide-y divide-gray-100 dark:divide-white/5 overflow-y-auto max-h-[420px]">
                    <router-link v-for="cat in navCategories" :key="cat.id || cat.name" :to="`/products/category/${encodeURIComponent(cat.name)}`" class="group flex items-center justify-between px-5 py-3.5 bg-white dark:bg-[#0c121e] hover:bg-[#f6faf7] dark:hover:bg-[#0c1c13]/40 transition-all duration-200 cursor-pointer">
                      <div class="flex items-center min-w-0 gap-3.5">
                        <!-- Icon Circle -->
                        <div class="w-9 h-9 rounded-full bg-[#f4faf6] dark:bg-[#112419]/50 flex items-center justify-center shrink-0 border border-emerald-500/5">
                          <!-- HDPE / Generic Shed Icon -->
                          <svg v-if="getCategoryIcon(cat.name) === 'hdpe-shed'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 10l9-6 9 6" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 10v11M15 10v11" />
                            <circle cx="10.5" cy="15" r="0.75" fill="currentColor" />
                            <circle cx="13.5" cy="15" r="0.75" fill="currentColor" />
                          </svg>

                          <!-- PP Shed Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'pp-shed'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 10l9-6 9 6" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10" />
                            <rect x="9" y="11" width="6" height="6" rx="0.5" />
                            <path d="M12 11v6M9 14h6" />
                          </svg>

                          <!-- Metal Shed Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'metal-shed'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 10l9-6 9 6" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 13h12M6 16h12" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v11" />
                          </svg>

                          <!-- Plastic Cabinet Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'plastic-cabinet'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <rect x="5" y="3" width="14" height="18" rx="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <line x1="12" y1="3" x2="12" y2="21" stroke-linecap="round" stroke-linejoin="round" />
                            <line x1="5" y1="12" x2="19" y2="12" stroke-linecap="round" stroke-linejoin="round" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M10 8v2M10 14v2" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M14 8v2M14 14v2" />
                          </svg>

                          <!-- Outdoor Box Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'outdoor-box'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <rect x="4" y="6" width="16" height="13" rx="1" stroke-linecap="round" stroke-linejoin="round" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4 10h16M4 14h16" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18v1H3z" />
                            <rect x="11" y="8" width="2" height="3" rx="0.5" fill="currentColor" />
                          </svg>

                          <!-- Greenhouse Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'greenhouse'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 2L3 9v11a1 1 0 001 1h16a1 1 0 001-1V9l-9-7z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 2v19" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 15c-1.5-1.5-3-1-3-3s1.5-1.5 3 0c1.5-1.5 3 0 3 2s-1.5 1.5-3 1z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 12v3" />
                          </svg>

                          <!-- Toolbox Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'toolbox'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5V3a1 1 0 011-1h4a1 1 0 011 1v2" />
                            <rect x="4" y="9" width="16" height="12" rx="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 5.5h18v3.5H3z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3" />
                            <circle cx="12" cy="13.5" r="0.75" fill="currentColor" />
                          </svg>

                          <!-- Carport Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'carport'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 13c3-3 6-4 9-4s6 1 9 4" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13v8" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 13v8" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M7 19h10v-2.5c0-.8-.7-1.5-1.5-1.5h-7c-.8 0-1.5.7-1.5 1.5V19z" />
                            <circle cx="9" cy="20" r="1" />
                            <circle cx="15" cy="20" r="1" />
                          </svg>

                          <!-- Accessories Icon -->
                          <svg v-else-if="getCategoryIcon(cat.name) === 'accessories'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <circle cx="12" cy="12" r="4.5" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
                            <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                          </svg>

                          <!-- Default / Fallback Icon -->
                          <svg v-else class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                            <line x1="12" y1="12" x2="12" y2="16" stroke-linecap="round" />
                            <circle cx="12" cy="11" r="0.5" fill="currentColor" />
                          </svg>
                        </div>

                        <!-- Labels -->
                        <div class="text-left min-w-0">
                          <div class="text-[13px] font-bold text-slate-800 dark:text-slate-100 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors leading-snug truncate">
                            {{ cat.name }}
                          </div>
                          <div v-if="cat.description" class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 leading-snug font-medium truncate">
                            {{ cat.description }}
                          </div>
                        </div>
                      </div>

                      <!-- Right Chevron -->
                      <svg class="w-3 h-3 text-gray-300 group-hover:text-emerald-500 dark:group-hover:text-emerald-400 transition-all duration-200 group-hover:translate-x-0.5 shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </router-link>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <router-link to="/services" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate inline-flex items-center gap-1', isActiveRoute('/services') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            บริการ
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/services') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>

          <router-link to="/projects" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate', isActiveRoute('/projects') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            ผลงานของเรา
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/projects') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>

          <router-link to="/blog" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate', isActiveRoute('/blog') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            บทความ
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/blog') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>

          <router-link to="/about" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate', isActiveRoute('/about') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            เกี่ยวกับเรา
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/about') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>

          <router-link to="/contact" :class="['text-xs xl:text-sm font-bold tracking-wide transition-colors duration-300 relative py-1 group truncate', isActiveRoute('/contact') ? (isDarkHeader ? 'text-emerald-400' : 'text-emerald-600 dark:text-emerald-400') : isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400']">
            ติดต่อเรา
            <span :class="['absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 rounded-full', isActiveRoute('/contact') ? 'w-full' : 'w-0 group-hover:w-full']"></span>
          </router-link>
        </nav>

        <!-- Desktop Actions -->
        <div class="hidden lg:flex items-center gap-3.5 z-10 shrink-0">
          <!-- Search -->
          <button @click="isSearchOpen = true" aria-label="ค้นหาสินค้า" :class="['transition-all p-2 rounded-full border border-transparent duration-300', isDarkHeader ? 'text-gray-300 hover:text-emerald-400 hover:bg-white/10' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-100/50 dark:hover:bg-white/5']">
            <svg class="w-[21px] h-[21px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </button>
          
          <!-- Cart -->
          <router-link v-if="settingsStore.isOnlineShoppingEnabled" to="/cart" aria-label="ตะกร้าสินค้า" :class="['relative transition-all p-2 rounded-full border border-transparent duration-300', isDarkHeader ? 'text-gray-300 hover:text-emerald-400 hover:bg-white/10' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-100/50 dark:hover:bg-white/5']">
            <svg class="w-[21px] h-[21px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span v-if="cartStore.cartTotal > 0" class="absolute top-0 right-0 bg-red-500 text-white text-[9.5px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-[#0a0f16] shadow-sm transform translate-x-1 -translate-y-1">
              {{ cartStore.cartTotal > 99 ? '99' : cartStore.cartTotal }}
            </span>
          </router-link>

          <!-- Dark/Light Toggle Directly in Topbar -->
          <button @click="toggleDarkMode" aria-label="สลับโหมดมืด" :class="['transition-all p-2 rounded-full border border-transparent duration-300', isDarkHeader ? 'text-gray-300 hover:text-emerald-400 hover:bg-white/10' : 'text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-100/50 dark:hover:bg-white/5']">
            <svg v-if="!isDark" class="w-[21px] h-[21px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
            <svg v-else class="w-[21px] h-[21px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
          </button>
          
          <!-- Divider -->
          <div :class="['w-px h-5', isDarkHeader ? 'bg-white/20' : 'bg-gray-200 dark:bg-white/10']"></div>

          <!-- User Account Dynamic Section (Saves desktop space and makes it extremely modern) -->
          <template v-if="settingsStore.isOnlineShoppingEnabled">
            <!-- Profile dropdown if authenticated -->
            <div v-if="authStore.isAuthenticated" class="relative" @mouseenter="openProfileDropdown" @mouseleave="closeProfileDropdown">
              <button aria-label="เมนูโปรไฟล์ผู้ใช้" class="flex items-center gap-2 p-0.5 rounded-full hover:bg-gray-100/50 dark:hover:bg-white/5 transition-all duration-300 focus:outline-none">
                <span class="sr-only">เมนูโปรไฟล์ผู้ใช้</span>
                <img v-if="authStore.currentUser?.avatar_url && !desktopAvatarError" :src="authStore.currentUser?.avatar_url" @error="desktopAvatarError = true" class="w-8 h-8 rounded-full object-cover shadow-sm bg-white border border-emerald-500/20">
                <div v-else class="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                  {{ authStore.currentUser?.first_name?.charAt(0)?.toUpperCase() || authStore.currentUser?.username?.charAt(0)?.toUpperCase() || 'U' }}
                </div>
              </button>
              
              <transition name="dropdown">
                <div v-if="isProfileDropdownOpen" class="absolute right-0 top-full pt-4 z-50">
                  <div class="bg-white dark:bg-[#0c121e] rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.12)] dark:shadow-[0_20px_40px_rgba(0,0,0,0.5)] border border-gray-100 dark:border-white/5 p-1.5 min-w-[210px] overflow-hidden">
                    <div class="px-3.5 py-3 border-b border-gray-50 dark:border-white/5">
                      <p class="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider">บัญชีผู้ใช้</p>
                      <p class="text-xs font-bold text-gray-800 dark:text-white truncate mt-0.5">{{ authStore.currentUser?.first_name }} {{ authStore.currentUser?.last_name }}</p>
                    </div>
                    <div class="p-1 space-y-0.5">
                      <router-link to="/profile" class="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        ข้อมูลส่วนตัว
                      </router-link>
                      <router-link to="/profile?tab=orders" class="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                        การสั่งซื้อของฉัน
                      </router-link>
                      <router-link v-if="settingsStore.isWishlistEnabled" to="/profile?tab=wishlist" class="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                        รายการโปรด
                      </router-link>
                      <button @click="authStore.logout(); isProfileDropdownOpen = false" class="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-left focus:outline-none">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        ออกจากระบบ
                      </button>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
            
            <!-- Sleek Glassmorphic Login Button for Guests -->
            <button v-else @click="isLoginModalOpen = true" :class="['flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-300 border active:scale-95 shadow-sm', isDarkHeader ? 'border-white/30 hover:border-white text-white bg-white/10 hover:bg-white hover:text-slate-900' : 'border-emerald-600/20 dark:border-emerald-500/20 hover:border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50/40 dark:bg-emerald-950/10 hover:bg-emerald-600 hover:text-white dark:hover:bg-emerald-500 dark:hover:text-slate-900']">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              <span>เข้าสู่ระบบ</span>
            </button>
          </template>
        </div>

        <!-- Mobile Menu Toggles -->
        <div class="flex lg:hidden items-center gap-2 z-50">
          <button @click="isSearchOpen = true" aria-label="ค้นหาสินค้า" :class="['transition-colors p-2 rounded-full', isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300']">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </button>
          <router-link v-if="settingsStore.isOnlineShoppingEnabled" to="/cart" aria-label="ตะกร้าสินค้า" :class="['relative transition-colors p-2 rounded-full', isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300']">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span v-if="cartStore.cartTotal > 0" class="absolute top-0 right-0 bg-red-500 text-white text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center border-2 border-white dark:border-[#0a0f16] shadow-sm transform translate-x-0.5 -translate-y-0.5">
              {{ cartStore.cartTotal > 99 ? '99' : cartStore.cartTotal }}
            </span>
          </router-link>

          <button @click="toggleDarkMode" aria-label="สลับโหมดมืด" :class="['transition-colors p-2 rounded-full', isDarkHeader ? 'text-gray-300 hover:text-emerald-400' : 'text-gray-700 dark:text-gray-300']">
            <svg v-if="!isDark" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
            <svg v-else class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
          </button>
          
          <!-- Hamburger Button (Only on Mobile) -->
          <button @click="toggleMobileMenu" aria-label="เมนูหลัก" :class="['focus:outline-none p-2 -mr-2 transition-colors', isDarkHeader ? 'text-white' : 'text-gray-900 dark:text-white']">
            <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            <svg v-else class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
      </div>
 
      <!-- Mobile Dropdown Menu -->
      <transition name="slide-down">
        <div v-if="isMobileMenuOpen" class="absolute top-full left-0 w-full bg-white/95 dark:bg-[#0a0f16]/95 backdrop-blur-xl border-b border-gray-100 dark:border-white/5 shadow-2xl py-6 flex flex-col px-6 space-y-4 z-40">
          <div class="lg:hidden">
            <button @click="isMobileProductsOpen = !isMobileProductsOpen" :class="['py-2 text-lg font-bold tracking-wide transition-colors flex items-center justify-between w-full', isActiveRoute('/products') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">
              สินค้า
              <svg class="w-4 h-4 transition-transform duration-200" :class="isMobileProductsOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
            </button>
            <div v-if="isMobileProductsOpen" class="pl-6 pb-2 space-y-1">
              <router-link to="/products" class="block py-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">ดูทั้งหมด</router-link>
              <router-link v-for="cat in navCategories" :key="cat.id || cat.name" :to="`/products/category/${encodeURIComponent(cat.name)}`" class="block py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium">
                {{ cat.name }}
              </router-link>
            </div>
          </div>
          <router-link to="/services" :class="['lg:hidden py-2 text-lg font-bold tracking-wide transition-colors', isActiveRoute('/services') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">บริการ</router-link>
          <router-link to="/projects" :class="['lg:hidden py-2 text-lg font-bold tracking-wide transition-colors', isActiveRoute('/projects') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">ผลงานของเรา</router-link>
          <router-link to="/blog" :class="['lg:hidden py-2 text-lg font-bold tracking-wide transition-colors', isActiveRoute('/blog') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">บทความ</router-link>
          <router-link to="/about" :class="['lg:hidden py-2 text-lg font-bold tracking-wide transition-colors', isActiveRoute('/about') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">เกี่ยวกับเรา</router-link>
          <router-link to="/contact" :class="['lg:hidden py-2 text-lg font-bold tracking-wide transition-colors', isActiveRoute('/contact') ? 'text-emerald-600 dark:text-emerald-400 border-l-4 border-emerald-500 pl-4' : 'text-gray-900 dark:text-white hover:text-emerald-600']">ติดต่อเรา</router-link>
          
          <div class="pt-6 mt-4 border-t border-gray-100 dark:border-white/10 flex flex-col gap-4">
             <template v-if="settingsStore.isOnlineShoppingEnabled">
                <template v-if="authStore.isAuthenticated">
                  <div class="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">
                     <img v-if="authStore.currentUser?.avatar_url && !mobileAvatarError" :src="authStore.currentUser?.avatar_url" @error="mobileAvatarError = true" class="w-12 h-12 rounded-full object-cover shadow-sm bg-white">
                     <div v-else class="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white text-lg font-black shadow-sm">
                       {{ authStore.currentUser?.first_name?.charAt(0)?.toUpperCase() || authStore.currentUser?.username?.charAt(0)?.toUpperCase() || 'U' }}
                     </div>
                    <div class="overflow-hidden">
                      <p class="text-sm font-bold text-gray-900 dark:text-white truncate">{{ authStore.currentUser?.first_name }} {{ authStore.currentUser?.last_name }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ authStore.currentUser?.email }}</p>
                    </div>
                  </div>
                  <router-link to="/profile" @click="isMobileMenuOpen = false" class="block w-full text-left py-2 text-base font-bold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">บัญชีของฉัน</router-link>
                  <router-link to="/profile?tab=orders" @click="isMobileMenuOpen = false" class="block w-full text-left py-2 text-base font-bold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">การสั่งซื้อของฉัน</router-link>
                  <router-link v-if="settingsStore.isWishlistEnabled" to="/profile?tab=wishlist" @click="isMobileMenuOpen = false" class="block w-full text-left py-2 text-base font-bold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">รายการโปรด</router-link>
                  <button @click="authStore.logout(); isMobileMenuOpen = false" class="w-full text-left py-2 text-base font-bold text-red-500 hover:text-red-600 transition-colors">ออกจากระบบ</button>
                </template>
                <template v-else>
                  <button @click="isLoginModalOpen = true; isMobileMenuOpen = false" class="block w-full text-center py-3.5 rounded-xl border-2 border-emerald-600 text-emerald-600 dark:border-emerald-500 dark:text-emerald-400 font-bold tracking-wide transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/10">
                    เข้าสู่ระบบ / สมัครสมาชิก
                  </button>
                </template>
             </template>
             <router-link to="/quotation" @click="isMobileMenuOpen = false" class="flex justify-center items-center py-4 rounded-xl bg-gray-900 dark:bg-emerald-600 text-white dark:text-white font-bold shadow-lg shadow-gray-900/10 active:scale-95 transition-transform uppercase tracking-widest text-sm">
               ขอใบเสนอราคาด่วน
             </router-link>
          </div>
        </div>
      </transition>
    </header>
    <!-- Page Content -->
    <main class="flex-grow min-h-screen relative z-10 flex flex-col">
      <router-view v-slot="{ Component, route }">
        <div :key="route.path" class="flex-grow flex flex-col w-full">
          <component :is="Component" />
        </div>
      </router-view>
    </main>

    <!-- Global Utilities -->
    <ToastContainer />
    <ConfirmModal />
    <AIProductRecommendation v-if="!isAdminRoute && route.path !== '/about' && settingsStore.isAiRecommendationEnabled" />
    <CookieBanner v-if="!isAdminRoute" />
    <GlobalSearch v-model="isSearchOpen" />
    <LoginModal :isOpen="isLoginModalOpen" @close="isLoginModalOpen = false" />
    <BackToTop v-if="!isAdminRoute" />
    <CompareFloatingBar v-if="!isAdminRoute" />

    <!-- Ultra Modern Premium Footer v4 -->
    <footer v-if="!isAdminRoute" class="relative z-0 bg-white dark:bg-[#0c121e] border-t border-gray-150 dark:border-white/5 pt-12 overflow-hidden select-none">
      <!-- Glow -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none -z-10">
        <div class="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-emerald-500/10 dark:bg-emerald-500/5 blur-[120px] rounded-full"></div>
        <div class="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-teal-500/10 dark:bg-teal-500/5 blur-[120px] rounded-full"></div>
      </div>

      <!-- CTA Banner Container -->
      <div class="max-w-[90rem] mx-auto px-6 lg:px-12 mb-12">
        <div class="w-full bg-[#f8fafc] dark:bg-slate-900/60 border border-slate-100 dark:border-white/5 rounded-2xl md:rounded-[2rem] shadow-[0_15px_40px_rgba(0,0,0,0.02)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.2)] overflow-hidden relative">
          <div class="grid grid-cols-1 lg:grid-cols-12 items-center min-h-[220px]">
            <!-- Left Content Area (col-span-7) -->
            <div class="p-6 md:p-8 lg:p-10 lg:col-span-7 flex flex-col justify-center relative z-10 text-left">
              <div>
                <!-- Green Badge -->
                <div v-if="settingsStore.footerBannerBadge" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100/80 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 text-[10px] font-extrabold tracking-wider w-fit mb-3 whitespace-nowrap">
                  <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ settingsStore.footerBannerBadge }}</span>
                </div>

                <!-- Title -->
                <h2 class="text-xl md:text-2xl lg:text-3xl font-black text-slate-950 dark:text-white leading-tight mb-2 select-none" v-html="formattedFooterBannerTitle">
                </h2>

                <!-- Subtitle -->
                <p v-if="settingsStore.footerBannerSubtitle" class="text-xs text-slate-500 dark:text-slate-400 max-w-md leading-relaxed mb-5">
                  {{ settingsStore.footerBannerSubtitle }}
                </p>
              </div>

              <!-- Inline Subscription Form -->
              <div class="max-w-md w-full">
                <form @submit.prevent="submitNewsletter" class="relative">
                  <input type="text" name="b_name" tabindex="-1" v-model="newsletterHoneypot" class="hidden">
                  <template v-if="newsletterSuccess">
                     <div class="w-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-full py-2 px-4 flex items-center justify-center gap-2 shadow-sm">
                       <div class="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                         <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path></svg>
                       </div>
                       <span class="text-xs font-bold text-emerald-700 dark:text-emerald-400">{{ settingsStore.footerBannerSuccess || 'ขอบคุณที่ติดตามข่าวสาร!' }}</span>
                     </div>
                  </template>
                  <template v-else>
                     <div class="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-full p-1 focus-within:ring-4 focus-within:ring-emerald-500/10 focus-within:border-emerald-500/60 transition-all duration-300 group">
                       <div class="pl-3 pr-0.5 text-slate-400 group-focus-within:text-emerald-500 transition-colors">
                         <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                           <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                         </svg>
                       </div>
                        <input 
                          type="email" 
                          v-model="newsletterEmail"
                          :placeholder="settingsStore.footerBannerPlaceholder || 'กรอกอีเมลของคุณที่นี่...'" 
                          class="w-full pl-2 pr-4 py-1.5 text-xs bg-transparent border-none focus:ring-0 outline-none text-slate-800 dark:text-white placeholder:text-slate-400"
                        >
                        <button 
                          type="submit" 
                          :disabled="newsletterLoading"
                          aria-label="ติดตามข่าวสาร"
                          class="px-5 py-2 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-extrabold rounded-full transition-all duration-300 flex items-center justify-center gap-1.5 disabled:opacity-70 active:scale-95 min-h-[36px] min-w-[36px]"
                        >
                          <svg v-if="newsletterLoading" class="animate-spin w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          <template v-else>
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            <span>{{ settingsStore.footerBannerButton || 'ติดตามข่าวสาร' }}</span>
                          </template>
                        </button>
                      </div>
                      <p v-if="newsletterError" class="absolute -bottom-5 left-5 text-[10px] text-red-500 font-semibold leading-none">{{ newsletterError }}</p>
                   </template>
                 </form>
               </div>
             </div>

             <!-- Right Image Area (col-span-5) -->
             <div class="relative hidden lg:block lg:col-span-5 self-stretch">
               <div class="absolute inset-0 bg-[#e2f0d9] dark:bg-emerald-950/20" style="clip-path: polygon(18% 0, 100% 0, 100% 100%, 0% 100%);"></div>
               <div class="absolute inset-0 bg-emerald-500/20" style="clip-path: polygon(17.5% 0, 19.5% 0, 2% 100%, 0% 100%);"></div>
               <div class="absolute inset-y-0 right-0 left-0 bg-cover bg-center" 
                    :style="{ backgroundImage: `url(${settingsStore.footerBannerImage || '/images/home/cta_greenhouse.png'})` }" 
                    style="clip-path: polygon(18.5% 0, 100% 0, 100% 100%, 1% 100%);">
               </div>
               <div class="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-emerald-800/60 to-transparent pointer-events-none opacity-80" style="clip-path: polygon(18.5% 0, 100% 0, 100% 100%, 1% 100%);"></div>
             </div>
           </div>
         </div>
       </div>

       <!-- Main Footer Links Section (Full Width Background) -->
       <div class="relative w-full bg-[#fafbfc] dark:bg-[#0c121e] border-t border-slate-100 dark:border-white/5 py-12 select-none">
         <div class="absolute top-0 left-0 w-64 h-64 text-emerald-600/5 dark:text-emerald-500/5 pointer-events-none select-none -z-10 translate-x-[-10%] translate-y-[-10%]">
           <svg viewBox="0 0 100 100" fill="currentColor" class="w-full h-full">
             <path d="M20,80 Q30,50 60,40 Q50,65 20,80" />
             <path d="M20,80 Q45,40 80,20 Q65,55 20,80" />
             <path d="M20,80 Q15,60 35,50 Q30,70 20,80" />
             <path d="M20,80 Q25,55 50,45 Q40,70 20,80" />
           </svg>
         </div>
         <div class="absolute right-0 bottom-0 top-0 w-1/3 opacity-[0.04] dark:opacity-[0.03] pointer-events-none bg-cover bg-right-bottom mix-blend-multiply dark:mix-blend-screen -z-10" :style="{ backgroundImage: `url(${settingsStore.footerBannerImage || '/images/home/cta_greenhouse.png'})` }"></div>

         <div class="max-w-[90rem] mx-auto px-6 lg:px-12">
           <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
             <!-- Left Column (col-span-4) -->
             <div class="lg:col-span-4 flex flex-col items-start lg:border-r lg:border-gray-150/80 dark:lg:border-white/5 lg:pr-10 text-left">
               <router-link to="/" class="group inline-flex items-center gap-2.5 mb-5 select-none">
                 <!-- Logo and domain -->
                 <template v-if="settingsStore.storeLogo">
                   <img :src="settingsStore.storeLogo" alt="Logo" class="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
                 </template>
                 <template v-else>
                   <div class="w-9 h-9 flex items-center justify-center bg-emerald-50/10 dark:bg-emerald-950/10 rounded-xl flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                     <svg class="w-8 h-8 text-emerald-600 dark:text-emerald-400" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                       <path d="M50 15 L85 45 L85 85 L15 85 L15 45 Z" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                       <path d="M50 15 L50 85" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                       <path d="M15 50 L85 50" stroke="currentColor" stroke-width="4" stroke-linecap="round" />
                       <path d="M50 55 C55 45 65 50 60 60 C55 67 50 70 50 70 C50 70 45 67 40 60 C35 50 45 45 50 55 Z" fill="#10b981" />
                     </svg>
                   </div>
                   <div class="flex items-center gap-0.5">
                     <span class="text-base font-extrabold text-emerald-600 dark:text-emerald-400 leading-none tracking-tight">โรงเรือนปลูกพืช</span>
                     <span class="text-base font-extrabold text-amber-500 leading-none tracking-tight">.com</span>
                   </div>
                 </template>
               </router-link>
            <p class="text-xs md:text-sm leading-relaxed text-gray-500 dark:text-gray-400 mb-5 max-w-sm">
              {{ settingsStore.storeDescription || 'ผู้นำด้านนวัตกรรมโรงเรือนอัจฉริยะ และระบบควบคุมสภาพแวดล้อมการเพาะปลูกอัตโนมัติด้วย AI และ IoT' }}
            </p>
            <div class="w-16 h-[3px] bg-emerald-600 dark:bg-emerald-500 rounded-full mb-6"></div>
            <div v-if="settingsStore.storeAddress || settingsStore.storeTaxId" class="flex flex-col gap-4 w-full">
              <div v-if="settingsStore.storeAddress" class="flex items-start gap-3.5 p-3 rounded-2xl bg-gray-50/50 dark:bg-slate-800/30 border border-gray-100/50 dark:border-white/5 shadow-sm">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/45 flex items-center justify-center text-emerald-600 dark:text-emerald-450 flex-shrink-0">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                </div>
                <div class="text-left flex-1">
                  <div class="text-xs font-black text-emerald-700 dark:text-emerald-455 mb-0.5">ที่อยู่</div>
                  <div class="text-xs text-gray-655 dark:text-gray-400 leading-relaxed">{{ settingsStore.storeAddress }}</div>
                </div>
              </div>
              <div v-if="settingsStore.storeTaxId" class="flex items-start gap-3.5 p-3 rounded-2xl bg-gray-50/50 dark:bg-slate-800/30 border border-gray-100/50 dark:border-white/5 shadow-sm">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/45 flex items-center justify-center text-emerald-600 dark:text-emerald-455 flex-shrink-0">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <div class="text-left flex-1">
                  <div class="text-xs font-black text-emerald-700 dark:text-emerald-455 mb-0.5">เลขประจำตัวผู้เสียภาษี</div>
                  <div class="text-xs text-gray-655 dark:text-gray-400 leading-relaxed">{{ settingsStore.storeTaxId }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Middle Column 1 (Main Menu) -->
          <div class="lg:col-span-2 flex flex-col lg:border-r lg:border-gray-150/80 dark:lg:border-white/5 lg:pr-8 text-left">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-9 h-9 rounded-xl bg-emerald-800 dark:bg-emerald-750 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <rect x="3" y="3" width="7" height="7" rx="1.5"></rect>
                  <rect x="14" y="3" width="7" height="7" rx="1.5"></rect>
                  <rect x="14" y="14" width="7" height="7" rx="1.5"></rect>
                  <rect x="3" y="14" width="7" height="7" rx="1.5"></rect>
                </svg>
              </div>
              <h3 class="text-base font-black text-gray-900 dark:text-white">เมนูหลัก</h3>
            </div>
            <ul class="flex flex-col gap-4">
              <li v-for="(item, i) in mainMenuLinks" :key="'main-link-' + i">
                <router-link :to="item.link" class="text-xs md:text-sm font-bold text-gray-555 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-455 transition-colors flex items-center gap-2.5 w-fit group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:scale-125 transition-transform flex-shrink-0"></span>
                  {{ item.name }}
                </router-link>
              </li>
            </ul>
          </div>
            <div class="lg:col-span-2 flex flex-col lg:border-r lg:border-gray-150/80 dark:lg:border-white/5 lg:pr-8 text-left">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-9 h-9 rounded-xl bg-emerald-800 dark:bg-emerald-750 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V9a6 6 0 00-12 0v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="text-base font-black text-gray-900 dark:text-white">ข้อมูลช่วยเหลือ</h3>
              </div>
              <ul class="flex flex-col gap-4">
                <li v-for="(item, i) in helpMenuLinks" :key="'help-link-' + i">
                  <router-link :to="item.link" class="text-xs md:text-sm font-bold text-gray-555 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-455 transition-colors flex items-center gap-2.5 w-fit group">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:scale-125 transition-transform flex-shrink-0"></span>
                    {{ item.name }}
                  </router-link>
                </li>
              </ul>
            </div>
            <div class="lg:col-span-4 flex flex-col gap-6 text-left">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-emerald-800 dark:bg-emerald-750 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                </div>
                <h3 class="text-base font-black text-gray-900 dark:text-white">ติดต่อเรา</h3>
              </div>
              <div class="bg-white dark:bg-slate-800/40 border border-gray-100 dark:border-white/5 rounded-2xl p-4 shadow-sm flex flex-col gap-4">
                <div v-if="contactPhones.length > 0 || settingsStore.storePhone" class="flex items-start gap-3.5">
                  <div class="w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-455 flex-shrink-0 shadow-sm border border-emerald-100/30 dark:border-emerald-800/20">
                    <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </div>
                  <div class="text-left flex-1 min-w-0">
                    <div class="text-xs font-black text-emerald-700 dark:text-emerald-400 mb-1.5">โทรศัพท์</div>
                    <div class="flex flex-col gap-1.5">
                      <a v-for="(phone, i) in contactPhones" :key="i" :href="'tel:' + phone.value.replace(/[^0-9+]/g, '')" class="py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                        <span class="font-extrabold text-[13px] text-gray-900 dark:text-white">{{ phone.value }}</span>
                        <span v-if="phone.name" class="text-[11px] font-semibold text-gray-600 dark:text-gray-400">คุณ{{ phone.name.replace(/คุณ/g, '') }}</span>
                      </a>
                      <a v-if="!contactPhones.length && settingsStore.storePhone" :href="'tel:' + settingsStore.storePhone.replace(/[^0-9+]/g, '')" class="py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 inline-flex items-center text-xs font-extrabold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                        {{ settingsStore.storePhone }}
                      </a>
                    </div>
                  </div>
                </div>
                <div class="w-full h-px bg-gray-200/50 dark:bg-white/5"></div>
                <div v-if="contactEmails.length > 0" class="flex items-start gap-3.5">
                  <div class="w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-455 flex-shrink-0 shadow-sm border border-emerald-100/30 dark:border-emerald-800/20">
                    <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div class="text-left flex-1 min-w-0">
                    <div class="text-xs font-black text-emerald-700 dark:text-emerald-400 mb-1.5">อีเมล</div>
                    <div class="flex flex-col gap-1.5">
                      <a v-for="(email, i) in contactEmails" :key="i" :href="'mailto:' + email.value" class="py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 inline-flex items-center text-xs font-extrabold text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors truncate max-w-full">
                        {{ email.value }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="contactLines.length > 0" class="flex flex-col gap-2.5">
                <a v-for="(line, i) in contactLines" :key="i" :href="line.url || 'https://line.me/ti/p/~' + line.value.replace(/^@/, '')" target="_blank" class="group flex items-center gap-3.5 p-3.5 rounded-2xl bg-gradient-to-r from-emerald-800 to-emerald-500 hover:from-emerald-700 hover:to-emerald-400 text-white shadow-md shadow-emerald-900/10 hover:shadow-emerald-800/20 active:scale-[0.98] transition-all duration-300 border border-emerald-600/15">
                  <div class="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 text-[#06C755] shadow-sm group-hover:scale-105 transition-transform duration-300">
                    <svg viewBox="0 0 24 24" class="w-5 h-5 fill-current"><path d="M 19.365 9.863 c 0.349 0 0.63 0.285 0.63 0.631 0 0.345 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 c 0.349 0 0.63 0.283 0.63 0.63 0 0.344 -0.281 0.629 -0.63 0.629 h -2.386 c -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 h 2.386 c 0.346 0 0.627 0.285 0.627 0.63 0 0.349 -0.281 0.63 -0.63 0.63 H 17.61 v 1.125 h 1.755 z m -3.855 3.016 c 0 0.27 -0.174 0.51 -0.432 0.596 -0.064 0.021 -0.133 0.031 -0.199 0.031 -0.211 0 -0.391 -0.09 -0.51 -0.25 l -2.443 -3.317 v 2.94 c 0 0.344 -0.279 0.629 -0.631 0.629 -0.346 0 -0.626 -0.285 -0.626 -0.629 V 8.108 c 0 -0.27 0.173 -0.51 0.43 -0.595 0.06 -0.023 0.136 -0.033 0.194 -0.033 c 0.195 0 0.375 0.104 0.495 0.254 l 2.462 3.33 V 8.108 c 0 -0.345 0.282 -0.63 0.63 -0.63 c 0.345 0 0.63 0.285 0.63 0.63 v 4.771 z m -5.741 0 c 0 0.344 -0.282 0.629 -0.631 0.629 -0.345 0 -0.627 -0.285 -0.627 -0.629 V 8.108 c 0 -0.345 0.282 -0.63 0.627 -0.63 c 0.346 0 0.628 0.285 0.628 0.63 v 4.771 z m -2.466 0.629 H 4.917 c -0.345 0 -0.63 -0.285 -0.63 -0.629 V 8.108 c 0 -0.345 0.285 -0.63 0.63 -0.63 c 0.348 0 0.63 0.285 0.63 0.63 v 4.141 h 1.756 c 0.348 0 0.629 0.283 0.629 0.63 0 0.344 -0.282 0.629 -0.629 0.629 M 24 10.314 C 24 4.943 18.615 0.572 12 0.572 S 0 4.943 0 10.314 c 0 4.811 4.27 8.842 10.035 9.608 c 0.391 0.082 0.923 0.258 1.058 0.59 c 0.12 0.301 0.079 0.766 0.038 1.08 l -0.164 1.02 c -0.045 0.301 -0.24 1.186 1.049 0.645 c 1.291 -0.539 6.916 -4.078 9.436 -6.975 C 23.176 14.393 24 12.458 24 10.314"/></svg>
                  </div>
                  <div class="flex-grow text-left min-w-0">
                    <div class="text-[10px] font-bold text-emerald-100/90 leading-none">ไลน์ ออฟฟิเชียล</div>
                    <div class="text-[13px] font-black text-white mt-1.5 leading-none">@{{ line.value.replace(/^@/, '') }}</div>
                    <div v-if="line.name" class="text-[10.5px] text-emerald-100/75 mt-1 leading-none truncate">{{ line.name }}</div>
                  </div>
                  <div class="flex-shrink-0 text-[11px] font-black text-emerald-800 bg-white px-3 py-1.5 rounded-full group-hover:bg-white group-hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm border border-emerald-600/10">เพิ่มเพื่อน</div>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Bottom copyright/social bar (Full Width) -->
      <div class="bg-[#023E1E] dark:bg-[#022D16] border-t border-emerald-900/10 relative overflow-hidden select-none">
        <!-- leaf branch SVG absolute in bottom bar -->
        <div class="absolute bottom-0 right-0 w-24 h-24 text-emerald-800/15 dark:text-emerald-900/10 pointer-events-none select-none translate-x-[20%] translate-y-[20%]">
          <svg viewBox="0 0 100 100" fill="currentColor" class="w-full h-full rotate-[90deg]">
            <path d="M10,80 Q20,50 50,40 Q40,65 10,80" />
            <path d="M10,80 Q35,40 70,20 Q55,55 10,80" />
          </svg>
        </div>

        <div class="max-w-[90rem] mx-auto px-6 lg:px-12 py-7 flex flex-col lg:flex-row justify-between items-center gap-6 text-white">
          <!-- Left: Copyright section -->
          <div class="flex items-center gap-3.5 flex-1 justify-center lg:justify-start">
            <div class="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-450 flex-shrink-0">
              <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div class="text-left">
              <p class="text-xs text-emerald-100/90 leading-relaxed font-medium">
                &copy; {{ new Date().getFullYear() }} <span class="font-bold text-white">{{ copyrightName }}</span>.<br class="sm:hidden" /> สงวนลิขสิทธิ์
              </p>
            </div>
          </div>

          <!-- Divider -->
          <div class="hidden lg:block w-px h-8 bg-emerald-900/30 shrink-0"></div>

          <!-- Middle: Payment gateways -->
          <div class="flex flex-col items-center gap-1.5 flex-1 justify-center">
            <div class="text-[10px] font-bold text-emerald-250 uppercase tracking-widest leading-none mb-1">การชำระเงินปลอดภัย</div>
            <div class="flex items-center gap-2">
              <!-- Visa -->
              <div v-if="settingsStore.paymentIpayEnabled" class="h-6 w-11 bg-white rounded flex items-center justify-center p-1 shadow-sm opacity-90 hover:opacity-100 transition-opacity">
                <svg viewBox="0 0 38 12" class="h-2.5 text-[#1434CB]" fill="currentColor"><path d="M14.743 11.666L16.275 1.836h2.463l-1.532 9.83h-2.463zm8.397-9.585c-.911-.428-2.342-.71-3.955-.71-2.43 0-4.144 1.258-4.157 3.061-.02 1.332 1.229 2.078 2.164 2.525.962.459 1.282.753 1.282 1.163-.013.626-.78.917-1.498.917-1.255 0-1.924-.184-2.951-.628l-.42-.191-.346 2.072c.732.327 2.078.614 3.486.626 2.585 0 4.275-1.236 4.296-3.149.02-1.077-.638-1.89-2.062-2.551-.869-.434-1.401-.723-1.401-1.163.013-.414.475-.845 1.442-.845.811-.013 1.419.167 1.888.351l.228.103.339-1.996zM28.468 11.666h2.179l-1.921-9.83h-2.025c-.47 0-.853.261-1.036.685l-3.565 8.271.01-.044h2.573s.42-.113.514-.143h3.141c.079.034.298.143.298.143l-1.168.861zm-2.415-2.732l1.01-2.613c-.02.04.208-.544.331-.892.052-.14.099-.281.144-.419.014.041.026.084.037.126l.462 2.14c.032.148.067.3.1.455h-2.084zm-14.887 2.732h-2.48l-1.579-6.666c-.198-.813-.811-1.406-1.547-1.637l-2.124-.467v-.218h3.585c.613 0 1.169.406 1.321 1.054l1.229 5.867 1.942-7.933h2.56l-2.907 9.83z"/></svg>
              </div>
              <!-- Mastercard -->
              <div v-if="settingsStore.paymentIpayEnabled" class="h-6 w-11 bg-white rounded flex items-center justify-center p-1 shadow-sm opacity-90 hover:opacity-100 transition-opacity">
                <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><circle cx="9" cy="12" r="5.5" fill="#eb001b"/><circle cx="15" cy="12" r="5.5" fill="#f79e1b"/><path d="M12 16.5a5.485 5.485 0 010-9 5.485 5.485 0 010 9z" fill="#ff5f00"/></svg>
              </div>
              <!-- PromptPay -->
              <div class="h-6 w-14 bg-white rounded flex items-center justify-center p-0.5 shadow-sm opacity-90 hover:opacity-100 transition-opacity">
                <svg viewBox="0 0 115 24" class="h-3.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 18 C6.48 18 2 13.52 2 8 C2 2.48 6.48 -2 12 -2" stroke="#003b70" stroke-width="2.5" stroke-linecap="round"/>
                  <text x="24" y="17" font-family="system-ui, -apple-system, sans-serif" font-weight="950" font-size="15" fill="#003b70">Prompt</text>
                  <text x="83" y="17" font-family="system-ui, -apple-system, sans-serif" font-weight="950" font-size="15" fill="#00a1e4">Pay</text>
                </svg>
              </div>
              <!-- Secure Check Shield Badge -->
              <div class="flex items-center gap-1 text-[9.5px] text-[#026943] dark:text-emerald-300 font-black bg-white/95 px-2.5 py-1 rounded-full shadow-sm border border-emerald-500/10">
                <svg class="w-3 h-3 text-[#026943] dark:text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>ปลอดภัย 100%</span>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div class="hidden lg:block w-px h-8 bg-emerald-900/30 shrink-0"></div>

          <!-- Right: Social follows -->
          <div class="flex flex-col items-center lg:items-end gap-1.5 flex-1 justify-center lg:justify-end">
            <div class="text-[10px] font-bold text-emerald-250 uppercase tracking-widest leading-none mb-1">ติดตามเราบนโซเชียล</div>
            <div class="flex items-center gap-3 relative z-10">
              <a v-if="contactFacebookUrl" :href="contactFacebookUrl" aria-label="Facebook" target="_blank" class="w-8.5 h-8.5 rounded-full border border-emerald-800 hover:bg-emerald-800/60 flex items-center justify-center text-gray-200 hover:text-white transition-all duration-300">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a v-if="contactTiktokUrl" :href="contactTiktokUrl" aria-label="TikTok" target="_blank" class="w-8.5 h-8.5 rounded-full border border-emerald-800 hover:bg-emerald-800/60 flex items-center justify-center text-gray-200 hover:text-white transition-all duration-300">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.76-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.16-3.44-3.37-3.46-5.7-.02-1.29.3-2.58.95-3.71A5.98 5.98 0 015.65 10.7a6.002 6.002 0 015.54-2.12v4.11c-.57-.02-1.14.07-1.68.25-.92.32-1.74 1.02-2.13 1.91-.32.74-.35 1.56-.16 2.33.15.63.47 1.25.93 1.7.9.89 2.3 1.21 3.52.8.93-.31 1.74-1.09 2.05-2.02.16-.48.26-1.01.24-1.52.01-4.71.01-9.42.01-14.13z"/></svg>
              </a>
              <a v-if="contactYoutubeUrl" :href="contactYoutubeUrl" aria-label="YouTube" target="_blank" class="w-8.5 h-8.5 rounded-full border border-emerald-800 hover:bg-emerald-800/60 flex items-center justify-center text-gray-200 hover:text-white transition-all duration-300">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <HistoryFloatingButton v-if="!isAdminRoute" />
    <ContactFloatingButton v-if="!isAdminRoute" />
    </template>
  </div>
</template>

<style>

/* Global Transitions */

.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Dropdown transition */
.dropdown-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Base custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: transparent; 
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1; 
  border-radius: 4px;
}
.dark ::-webkit-scrollbar-thumb {
  background: #334155; 
}

/* Transparent nav text shadow for readability over hero images */
.nav-text-shadow a,
.nav-text-shadow button,
.nav-text-shadow nav a,
.nav-text-shadow nav button {
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.6), 0 0 8px rgba(255, 255, 255, 0.3);
}
.nav-text-shadow svg {
  filter: drop-shadow(0 1px 2px rgba(255, 255, 255, 0.5));
}
.dark .nav-text-shadow a,
.dark .nav-text-shadow button,
.dark .nav-text-shadow nav a,
.dark .nav-text-shadow nav button,
.is-dark-header.nav-text-shadow a,
.is-dark-header.nav-text-shadow button,
.is-dark-header.nav-text-shadow nav a,
.is-dark-header.nav-text-shadow nav button {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7), 0 0 10px rgba(0, 0, 0, 0.4) !important;
}
.dark .nav-text-shadow svg,
.is-dark-header.nav-text-shadow svg {
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6)) !important;
}
</style>
